import { useAuth0 } from "@auth0/auth0-react";
import { useMutation } from "@tanstack/react-query";
import { Header, createColumnHelper } from "@tanstack/react-table";
import _ from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { ButtonGroup, ButtonToolbar, Col, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import type { BillingController } from "../../../api/src/billing/billing.controller";
import type { FeeStructure } from "../../../api/src/billing/lib";
import type { UnpackResponse } from "../../../api/src/lib";
import Loading from "../Loading";
import { useQueryAllAccounts } from "../clients/account/lib";
import { useQueryHouseholds } from "../clients/household/lib";
import ActionButton from "../components/ActionButton";
import { useErrorMessage } from "../components/FormError";
import IndeterminateCheckbox from "../components/IndeterminateCheckbox";
import MultiSelector from "../components/MultiSelector";
import TabContainerWithTabs from "../components/TabContainer";
import { Table, useTable } from "../components/Table/Table";
import { multiSelectIncludes } from "../components/Table/filters";
import { authenticatedFetch } from "../lib/api";
import BillingNav from "./BillingNav";
import {
  DELETE_FEE_STRUCTURES_MUTATION_KEY,
  useQueryFeeStructures,
} from "./lib";

type FeeStructureRow = Omit<FeeStructure, "firmId"> & {
  assignedAccounts: number[];
};

const columnHelper = createColumnHelper<FeeStructureRow>();

export const frequencyFilterOptions = [
  { label: "Monthly", value: 1 },
  { label: "Quarterly", value: 2 },
  { label: "Annual", value: 3 },
];
const FrequencyFilter = <TRow, TValue>({
  header,
}: {
  header: Header<TRow, TValue>;
}) => {
  const onChange = useCallback(
    (values: number[]) => {
      header.column.setFilterValue(values);
    },
    [header.column],
  );

  return (
    <MultiSelector
      options={frequencyFilterOptions}
      placeholder="All"
      setValue={onChange}
    />
  );
};

const FeeStructureTable = () => {
  const columns = useMemo(
    () => [
      columnHelper.display({
        id: "select",
        header: ({ table }) => (
          <IndeterminateCheckbox
            {...{
              checked: table.getIsAllRowsSelected(),
              indeterminate: table.getIsSomeRowsSelected(),
              onChange: table.getToggleAllRowsSelectedHandler(),
            }}
          />
        ),
        cell: ({ row }) => (
          <div className="px-1">
            <IndeterminateCheckbox
              {...{
                checked: row.getIsSelected(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </div>
        ),
      }),
      columnHelper.accessor((row) => row.name, {
        id: "name",
        cell: (info) => (
          <Link
            to={`/billing/fee-structure/${info.row.original.feeStructureId}`}
          >
            {info.getValue()}
          </Link>
        ),
        header: () => "Name",
      }),
      columnHelper.accessor(
        (row): string => {
          const option = frequencyFilterOptions.find(
            (a) => a.label === row.frequency,
          );
          return option?.value.toString() ?? "";
        },
        {
          id: "frequency",
          header: () => "Frequency",
          cell: (info) => {
            const option = frequencyFilterOptions.find(
              (a) => a.value.toString() === info.getValue(),
            );
            return option?.label ?? "";
          },
          filterFn: multiSelectIncludes,
          meta: {
            filterComponent: FrequencyFilter,
          },
        },
      ),
      columnHelper.accessor((row) => row.calculationType, {
        id: "calculation",
        header: () => "Collection",
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.collectionType, {
        id: "collection",
        header: () => "Structure",
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.balanceType, {
        id: "valuation",
        header: () => "Valuation Method",
        enableColumnFilter: false,
      }),
      columnHelper.accessor((row) => row.assignedAccounts.length, {
        id: "assignedAccounts",
        cell: (info) => (
          <Link
            to={`/clients/accounts?${info.row.original.assignedAccounts
              .map((id) => `id=${id}`)
              .join("&")}`}
          >
            {info.getValue()}
          </Link>
        ),
        header: () => "Assigned Accounts",
        enableColumnFilter: false,
        meta: {
          className: "text-end",
          headerClassName: "text-end",
        },
      }),
    ],
    [],
  );

  const { isPending: isPendingFeeStructures, data } = useQueryFeeStructures();

  const { isPending: isPendingAccounts, data: dataAccounts } =
    useQueryAllAccounts();
  const accounts = dataAccounts?.data;

  const { isPending: isPendingHouseholds, data: dataHouseholds } =
    useQueryHouseholds();
  const households = dataHouseholds?.data;

  const [feeStructures, setFeeStructures] = useState<FeeStructureRow[]>([]);

  useEffect(() => {
    if (!isPendingAccounts && !isPendingHouseholds) {
      const mapHouseholds = _.keyBy(households, "id");
      const mapAccountsFeeStructures = _.groupBy(
        accounts?.map((account) => ({
          id: account.id,
          feeStructureId:
            account.feeStructureId != null
              ? account.feeStructureId
              : account.householdId != null
                ? mapHouseholds[account.householdId]?.feeStructureId
                : undefined,
        })),
        "feeStructureId",
      );

      setFeeStructures(
        data?.data.map((feeStructure) => ({
          ...feeStructure,
          assignedAccounts:
            typeof feeStructure.feeStructureId !== "undefined"
              ? typeof mapAccountsFeeStructures[feeStructure.feeStructureId] ===
                "undefined"
                ? []
                : mapAccountsFeeStructures[feeStructure.feeStructureId].map(
                    (item) => item.id,
                  )
              : [],
        })) ?? [],
      );
    }
  }, [
    data?.data,
    setFeeStructures,
    accounts,
    households,
    isPendingAccounts,
    isPendingHouseholds,
  ]);

  const { table, rowSelection, setRowSelection } = useTable({
    columns,
    data: feeStructures,
  });

  const { getAccessTokenSilently } = useAuth0();

  const { setError, resetError, errorMessage } = useErrorMessage();

  // One `DELETE /v1/billing/fee-structures/:id` per selected row (APP-1651:
  // the bulk `POST /billing/fee-structures/delete` is removed).
  const removeFeeStructures = (ids: number[]) =>
    Promise.allSettled(
      ids.map((id) =>
        authenticatedFetch<
          UnpackResponse<BillingController["deleteFeeStructure"]>
        >(
          `${process.env.REACT_APP_API_ROOT}/v1/billing/fee-structures/${id}`,
          getAccessTokenSilently,
          { method: "DELETE" },
        )(),
      ),
    );

  // `useQueryFeeStructures` walks the list again when this settles
  // (`DELETE_FEE_STRUCTURES_MUTATION_KEY`), after a partial failure too: a
  // failed delete does not make it settle as an error. The selection is
  // cleared then as well: it holds row indexes, and the rows that are left take
  // new indexes when the list is read again.
  const del = useMutation({
    mutationKey: DELETE_FEE_STRUCTURES_MUTATION_KEY,
    mutationFn: async () => {
      if (Object.keys(rowSelection).length === 0) return;
      resetError();
      const results = await removeFeeStructures(
        data?.data.reduce((result, feeStructure, idx) => {
          return Object.keys(rowSelection).includes(`${idx}`)
            ? [...result, feeStructure.feeStructureId]
            : result;
        }, [] as number[]) ?? [],
      );
      setRowSelection({});
      const failed = results.filter((result) => result.status === "rejected");
      if (failed.length > 0) {
        const message = "Failed to delete fee structure";
        setError(message);
        console.error(message, failed);
      }
    },
  });

  return (
    <TabContainerWithTabs tabs={BillingNav}>
      {errorMessage}
      <Row>
        <Col className="d-flex justify-content-between mb-3">
          <ButtonToolbar className="gap-3">
            <ButtonGroup>
              <ActionButton
                as={Link}
                to="new"
                variant="secondary"
                label="Create"
                icon="/icons/new.svg"
              />
            </ButtonGroup>
            <ButtonGroup>
              <ActionButton
                variant="secondary"
                label="Delete"
                icon="/icons/trash.svg"
                onClick={() => del.mutate()}
                disabled={
                  del.isPending || Object.keys(rowSelection).length === 0
                }
              />
            </ButtonGroup>
          </ButtonToolbar>
        </Col>
      </Row>
      <Row>
        <Col>
          {isPendingFeeStructures ||
          isPendingAccounts ||
          isPendingHouseholds ? (
            <Loading />
          ) : (
            <Table table={table} />
          )}
        </Col>
      </Row>
    </TabContainerWithTabs>
  );
};

export default FeeStructureTable;
